import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, FileDown, RefreshCw } from "lucide-react";
import React from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import * as api from "@/api/returns";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { reasonLabel, reasonTone } from "@/lib/returns";

function fmtMoney(v: number): string {
  return v.toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtQty(v: number): string {
  return Number.isInteger(v)
    ? String(v)
    : v.toLocaleString("ru-RU", { maximumFractionDigits: 3 });
}

const ZERO_GUID = "00000000-0000-0000-0000-000000000000";

// Партия для строки документа: ZERO_GUID = ушло без партии (фолбэк).
function partiaCell(ref: string, name: string): string {
  return ref === ZERO_GUID ? "без партии" : name || "—";
}

// Сколько заказов показываем подписями, прежде чем свернуть в «и ещё N».
const ORDERS_SHOWN = 6;

/** Заказы покупателя документа. Их десятки (боевой рекорд приёмки — 90),
 *  поэтому в шапке карточки стоит ЧИСЛО, а подписи — списком под ним и с
 *  обрезкой: полный перечень заслонил бы сам документ, ради которого экран
 *  и открыт. */
function OrdersLine({ labels }: { labels: string[] }): React.ReactElement {
  if (labels.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">Заказ покупателя не указан</p>
    );
  }
  const shown = labels.slice(0, ORDERS_SHOWN);
  const rest = labels.length - shown.length;
  return (
    <p className="text-xs text-muted-foreground">
      Заказов покупателя: <span className="font-medium text-foreground">{labels.length}</span>
      {" · "}
      {shown.join(", ")}
      {rest > 0 && ` и ещё ${rest}`}
    </p>
  );
}

export function ProcessingDetailPage(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const sessionId = id!;
  const qc = useQueryClient();

  const sessionQuery = useQuery({
    queryKey: ["returns", "session", sessionId],
    queryFn: () => api.getSession(sessionId),
  });

  const stateQuery = useQuery({
    queryKey: ["returns", "processing", "state", sessionId],
    queryFn: () => api.getProcessingState(sessionId),
  });

  // Статусы run-а:
  //   `created`     — всё успешно, preview не нужен;
  //   `partial`     — есть упавшие группы → retry;
  //   `in_progress` — обработка застряла (упал процесс / деплой /
  //                   1С таймаут) → retry, тот же эффект что и partial.
  //   `null`        — обработка ещё не запускалась → preview.
  const runStatus = stateQuery.data?.status ?? null;
  const fullySucceeded = runStatus === "created";
  const hasPartialFailure = runStatus === "partial" || runStatus === "in_progress";
  const alreadyTried = !!stateQuery.data;

  const previewQuery = useQuery({
    queryKey: ["returns", "processing", "preview", sessionId],
    queryFn: () => api.getProcessingPreview(sessionId),
    enabled: !alreadyTried && !stateQuery.isLoading,
    retry: false,
  });

  const createMut = useMutation({
    mutationFn: () => api.createProcessing(sessionId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["returns", "processing", "state", sessionId] });
      toast.success("Документы созданы в 1С");
    },
    onError: (e: { response?: { data?: { detail?: string } }; message?: string }) => {
      // На shortage и т.п. ProcessingError бэк возвращает 400 — preview
      // мог устареть. Сбрасываем кэш preview, чтобы пользователь увидел
      // актуальную картину при следующей загрузке.
      qc.invalidateQueries({ queryKey: ["returns", "processing", "preview", sessionId] });
      toast.error(e.response?.data?.detail ?? e.message ?? "Ошибка создания");
    },
  });

  const session = sessionQuery.data;
  const preview = previewQuery.data;
  const createdDocs =
    stateQuery.data?.result_json?.created ?? createMut.data?.created ?? null;

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Обработка возвратов</h1>
        {session && (
          <p className="text-sm text-muted-foreground">
            {session.display_number
              ? `Приёмка #${session.display_number}`
              : "Приёмка"}{" "}
            от {new Date(session.created_at).toLocaleString("ru-RU")} ·{" "}
            {session.marketplace.toUpperCase()} · {session.items_count} поз.
          </p>
        )}
      </div>

      {previewQuery.isError && !alreadyTried && (
        <Card>
          <CardContent className="flex items-start gap-3 pt-6">
            <AlertCircle className="size-5 shrink-0 text-destructive" />
            <div className="space-y-2 text-sm">
              <div className="font-medium">Не удалось построить preview</div>
              <div className="text-muted-foreground">
                {(previewQuery.error as { response?: { data?: { detail?: string } } })
                  ?.response?.data?.detail ??
                  (previewQuery.error as Error)?.message ??
                  "Неизвестная ошибка"}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => previewQuery.refetch()}
              >
                <RefreshCw className="size-4" /> Повторить
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {previewQuery.isLoading && !alreadyTried && (
        <p className="text-sm text-muted-foreground">Считаем остатки на комиссии…</p>
      )}

      {preview && !alreadyTried && (
        <PreviewBlock
          preview={preview}
          onCreate={() => createMut.mutate()}
          creating={createMut.isPending}
        />
      )}

      {hasPartialFailure && (
        <Card className="border-yellow-400/60 bg-yellow-50/30">
          <CardContent className="flex flex-col gap-3 pt-6 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-2 text-sm">
              <AlertCircle className="size-5 shrink-0 text-yellow-700" />
              <div>
                <div className="font-medium">Не все документы созданы</div>
                <div className="text-muted-foreground">
                  Часть групп упала с ошибкой. Повтор создаст только упавшие —
                  успешные документы не будут дублироваться.
                </div>
              </div>
            </div>
            <Button
              onClick={() => createMut.mutate()}
              disabled={createMut.isPending}
            >
              <RefreshCw className="size-4" />
              {createMut.isPending ? "Повторяем…" : "Повторить упавшие"}
            </Button>
          </CardContent>
        </Card>
      )}

      {createdDocs && (
        <CreatedBlock sessionId={sessionId} created={createdDocs} />
      )}
      {fullySucceeded && createdDocs && (
        <p className="text-sm text-muted-foreground">
          Обработка завершена. Если нужно создать ещё документы — создайте новую
          приёмку.
        </p>
      )}
    </div>
  );
}

function PreviewBlock({
  preview,
  onCreate,
  creating,
}: {
  preview: api.PreviewResponse;
  onCreate: () => void;
  creating: boolean;
}): React.ReactElement {
  return (
    <div className="space-y-4">
      {preview.shortages.length > 0 && (
        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base text-destructive">
              <AlertCircle className="size-4" /> Нехватка остатков на комиссии
            </CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1">Номенклатура</th>
                  <th className="py-1">Хар-ка</th>
                  <th className="py-1">Партия</th>
                  <th className="py-1">Причина</th>
                  <th className="py-1 text-right">Нужно</th>
                  <th className="py-1 text-right">Есть</th>
                </tr>
              </thead>
              <tbody>
                {preview.shortages.map((s, i) => (
                  <tr key={i} className="border-t">
                    <td className="py-1">{s.nom_name}</td>
                    <td className="py-1">{s.char_name ?? "—"}</td>
                    <td className="py-1">{s.partia_name}</td>
                    <td className="py-1">
                      <span
                        className={
                          "rounded px-1.5 py-0.5 text-xs " + reasonTone(s.reason_type)
                        }
                      >
                        {reasonLabel(s.reason_type, s.reason_subtype)}
                      </span>
                    </td>
                    <td className="py-1 text-right">{fmtQty(s.needed)}</td>
                    <td className="py-1 text-right">{fmtQty(s.available)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {(preview.partia_fallbacks?.length ?? 0) > 0 && (
        <Card className="border-amber-400/60 bg-amber-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base text-amber-700">
              <AlertCircle className="size-4" /> Учтено без партии
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              По этим позициям партия есть в 1С, но остатка на комиссии по её
              заказам нет — закрыто остатком без партии. В документ возврата
              партия не пойдёт (ляжет без партии), но сохранена для служебок.
            </p>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1">Номенклатура</th>
                  <th className="py-1">Хар-ка</th>
                  <th className="py-1">Партия</th>
                  <th className="py-1 text-right">Без партии, кол-во</th>
                </tr>
              </thead>
              <tbody>
                {preview.partia_fallbacks!.map((f, i) => (
                  <tr key={i} className="border-t">
                    <td className="py-1">{f.nom_name}</td>
                    <td className="py-1">{f.char_name ?? "—"}</td>
                    <td className="py-1">{f.partia_name || "—"}</td>
                    <td className="py-1 text-right">{fmtQty(f.qty)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {preview.planned_documents.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Нет позиций для создания документов.
        </p>
      )}

      {preview.planned_documents.map((doc, i) => (
        <Card key={`${doc.organization_ref}-${i}`}>
          <CardHeader>
            <CardTitle className="text-base">
              {doc.organization_name}
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                · {doc.kontragent_name} · {doc.dogovor_name}
              </span>
            </CardTitle>
            <OrdersLine labels={doc.order_labels} />
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1">Номенклатура</th>
                  <th className="py-1">Хар-ка</th>
                  <th className="py-1">Партия</th>
                  <th className="py-1">Причина</th>
                  <th className="py-1 text-right">Кол-во</th>
                  <th className="py-1 text-right">Цена пер.</th>
                  <th className="py-1 text-right">Сумма</th>
                </tr>
              </thead>
              <tbody>
                {doc.lines.map((line, j) => (
                  <tr key={j} className="border-t">
                    <td className="py-1">{line.nom_name}</td>
                    <td className="py-1">{line.char_name ?? "—"}</td>
                    <td className="py-1">{partiaCell(line.partia_ref, line.partia_name)}</td>
                    <td className="py-1">
                      {line.reason_type && (
                        <span
                          className={
                            "rounded px-1.5 py-0.5 text-xs " + reasonTone(line.reason_type)
                          }
                        >
                          {/* Подтипы утиля в строке документа слиты — только тип. */}
                          {reasonLabel(line.reason_type, null)}
                        </span>
                      )}
                    </td>
                    <td className="py-1 text-right">{fmtQty(line.qty)}</td>
                    <td className="py-1 text-right">
                      {fmtMoney(line.unit_transfer_price)}
                    </td>
                    <td className="py-1 text-right">{fmtMoney(line.sum_total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t font-medium">
                  <td colSpan={6} className="py-1 text-right">
                    Итого:
                  </td>
                  <td className="py-1 text-right">{fmtMoney(doc.sum_total)}</td>
                </tr>
              </tfoot>
            </table>
          </CardContent>
        </Card>
      ))}

      <div className="flex justify-end">
        <Button onClick={onCreate} disabled={!preview.can_create || creating}>
          {creating ? "Создаём…" : `Создать ${preview.planned_documents.length} док.`}
        </Button>
      </div>
    </div>
  );
}

function CreatedBlock({
  sessionId,
  created,
}: {
  sessionId: string;
  created: api.CreatedDoc[];
}): React.ReactElement {
  const orgs = Array.from(
    new Map(
      created
        .filter((d) => !d.error)
        .map((d) => [d.organization_ref, d.organization_name]),
    ).entries(),
  );

  // Без обработчика axios-ошибка на скачивании СЗ отклоняла промис молча —
  // кнопка «ничего не делала». Показываем причину тостом. При responseType
  // 'blob' тело ошибки приходит Blob-ом — читаем его как текст.
  const onDownload = async (ref: string, name: string): Promise<void> => {
    try {
      await api.downloadMemoForOrg(sessionId, ref, name);
    } catch (e) {
      let msg = "Не удалось скачать СЗ";
      const data = (e as { response?: { data?: unknown } })?.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          if (typeof parsed?.detail === "string") msg = parsed.detail;
        } catch {
          /* тело не JSON — оставляем generic */
        }
      } else if (typeof (data as { detail?: string })?.detail === "string") {
        msg = (data as { detail: string }).detail;
      }
      toast.error(msg);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CheckCircle2 className="size-4 text-green-600" />
          Документы в 1С
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <ul className="space-y-2 text-sm">
          {created.map((d, i) => (
            <li
              key={`${d.organization_ref}-${d.zakaz_ref ?? ""}-${i}`}
              className="rounded-md border p-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium">{d.organization_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {d.orders_count != null ? (
                      <>Заказов покупателя: {d.orders_count}</>
                    ) : (
                      <>
                        Заказ:{" "}
                        {d.zakaz_label ? (
                          <span className="font-medium text-foreground">
                            {d.zakaz_label}
                          </span>
                        ) : (
                          <code className="rounded bg-muted px-1">{d.zakaz_ref}</code>
                        )}
                      </>
                    )}
                  </div>
                  {d.error ? (
                    <div className="mt-1 text-destructive">{d.error}</div>
                  ) : (
                    <div className="mt-1">
                      №{d.number ?? "—"} от{" "}
                      {d.date ? new Date(d.date).toLocaleDateString("ru-RU") : "—"}
                      {!d.posted && (
                        <span className="ml-2 rounded bg-yellow-100 px-1.5 py-0.5 text-xs text-yellow-900">
                          не проведён
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {!d.error && (
                  <div className="shrink-0 text-right text-sm font-medium">
                    {fmtMoney(d.sum_total)}
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>

        {orgs.length > 0 && (
          <div className="space-y-2 border-t pt-3">
            <p className="text-sm font-medium">Служебные записки по организациям</p>
            <div className="flex flex-wrap gap-2">
              {orgs.map(([ref, name]) => (
                <Button
                  key={ref}
                  variant="outline"
                  size="sm"
                  onClick={() => onDownload(ref, name)}
                >
                  <FileDown className="size-4" /> {name}
                </Button>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
